'use client';
import React, { useCallback, useState } from 'react';
import { ApiAttribute } from '@context/openapi/types';
import InputDescription from '@components/ui/DynamicForm/InputDescription';
import DateInput from '@components/ui/DynamicForm/DateInput';

interface InputFieldProps {
  field: ApiAttribute;
  onChange: (field: ApiAttribute, value: string) => void;
  ref?: React.Ref<HTMLInputElement>;
  onHelpVisible: (visible: boolean) => void;
  setIsFocused?: (visible: boolean) => void;
  isFocused?: boolean;
  isFilterValueInput?: boolean;
  disabled?: boolean;
}

const InputField: React.FC<InputFieldProps> = ({
  field,
  onChange,
  ref,
  onHelpVisible,
  setIsFocused,
  isFocused: _isFocused,
  isFilterValueInput = false,
  disabled = false,
}) => {
  const handleChange = useCallback(
    (id: string, value: string) => {
      onChange(field, value);
    },
    [field, onChange]
  );

  const [_isHelpVisible, setIsHelpVisible] = useState(false);

  if (field.type === 'DATE') {
    return (
      <DateInput
        field={field}
        onChange={onChange}
        onHelpVisible={onHelpVisible}
        setIsFocused={setIsFocused}
        isFocused={_isFocused}
        disabled={disabled}
      />
    );
  }

  return (
    <div
      className={`ml-auto relative overflow-visible flex items-center gap-1 justify-between flex-1 w-full z-10 opacity-100`}
    >
      <input
        ref={ref}
        id={field.key}
        name={field.key}
        type={field.type}
        placeholder={isFilterValueInput ? 'Value' : ''}
        value={(field.value || '') as string}
        onChange={(event) => {
          handleChange(field.key, event.target.value);
        }}
        onFocus={() => setIsFocused && setIsFocused(true)}
        onBlur={() => setIsFocused && setIsFocused(false)}
        required={field.required}
        className={`
          pl-2 pr-5 py-0.5 border-l border-form-border
          bg-white w-full
          appearance-none
          [&::-webkit-inner-spin-button]:appearance-none
          [&::-webkit-outer-spin-button]:appearance-none cursor-pointer
          text-sm
          placeholder:text-xs placeholder:text-neutral-400 placeholder:py-1
          focus-visible:outline-shade-aqua-300
          `}
        data-pendo-mask
        disabled={disabled}
      />
      <InputDescription
        description={field.description}
        onHelpVisible={(visible) => {
          setIsHelpVisible(visible);
          onHelpVisible(visible);
        }}
      />
    </div>
  );
};

export default InputField;
